import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { PageHeader, Badge, Modal, Field, Select, Tabs, Empty } from "@/components/Kit";
import { fmtDate } from "@/lib/date";
import WebsiteSettings from "./WebsiteSettings";
import { ClipboardList, Globe, FileText, CheckCircle2, XCircle, Eye } from "lucide-react";

const COLOR = { submitted: "#06B6D4", under_review: "#F59E0B", approved: "#10B981", rejected: "#F43F5E" };
const DOC_LABEL = { photo: "Photo", birth_cert: "Birth Cert", tc: "TC / Marks", aadhaar_doc: "Aadhaar" };

export default function Admissions() {
  const [tab, setTab] = useState("applications");
  return (
    <div data-testid="admissions-page">
      <PageHeader title="Online Admissions" subtitle="Applications from the public website and your school portal content" />
      <Tabs testid="adm-tabs" active={tab} onChange={setTab} tabs={[{ key: "applications", label: "Applications", icon: ClipboardList }, { key: "website", label: "Website Content", icon: Globe }]} />
      {tab === "applications" ? <Applications /> : <WebsiteSettings />}
    </div>
  );
}

function Applications() {
  const qc = useQueryClient();
  const [act, setAct] = useState(null); // {app, status}
  const [remarks, setRemarks] = useState("");
  const [section, setSection] = useState("A");
  const [filter, setFilter] = useState("");
  const { data: list = [] } = useQuery({ queryKey: ["admissions"], queryFn: async () => (await api.get("/school/admissions")).data });
  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/school/academics/classes")).data });

  const update = useMutation({
    mutationFn: async () => (await api.put(`/school/admissions/${act.app.id}/status`, { status: act.status, remarks, section })).data,
    onSuccess: (d) => { toast.success(`Application ${d.status.replace("_", " ")} · Parent notified on WhatsApp`); setAct(null); setRemarks(""); qc.invalidateQueries({ queryKey: ["admissions"] }); qc.invalidateQueries({ queryKey: ["students"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const rows = list.filter((a) => !filter || a.status === filter);
  const counts = list.reduce((m, a) => ({ ...m, [a.status]: (m[a.status] || 0) + 1 }), {});
  const sections = classes.find((c) => c.name === act?.app?.class_applying)?.sections || ["A", "B"];

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="adm-filters">
        {[["", "All", list.length], ...Object.keys(COLOR).map((k) => [k, k.replace("_", " "), counts[k] || 0])].map(([k, l, n]) => (
          <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition ${filter === k ? "bg-indigo-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-white"}`} data-testid={`adm-filter-${k || "all"}`}>{l} <span className="opacity-70">{n}</span></button>
        ))}
      </div>
      <div className="ep-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Application</th><th className="px-4 py-3">Student</th><th className="px-4 py-3">Class</th><th className="px-4 py-3">Parent</th><th className="px-4 py-3">Documents</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`adm-row-${a.application_no}`}>
                  <td className="px-4 py-2.5"><p className="font-mono text-xs text-indigo-300">{a.application_no}</p><p className="text-[11px] text-slate-500">{fmtDate(a.created_at)}</p></td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      {a.documents?.photo ? <img src={fileUrl(`/api/public/files/${a.documents.photo}`)} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-700" /> : <div className="h-9 w-9 rounded-lg bg-slate-800" />}
                      <div><p className="font-medium text-white">{a.student_name}</p><p className="text-[11px] text-slate-500">{a.gender} · {fmtDate(a.dob)}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><Badge>{a.class_applying}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-400">{a.parent_name}<br /><span className="text-xs">{a.parent_phone}</span></td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(a.documents || {}).filter(([, v]) => v).map(([k, v]) => (
                        <a key={k} href={fileUrl(`/api/public/files/${v}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-slate-800 px-2 py-0.5 text-[11px] text-slate-300 hover:text-white" data-testid={`doc-link-${a.application_no}-${k}`}><FileText size={11} /> {DOC_LABEL[k] || k}</a>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><Badge color={COLOR[a.status]}>{a.status.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-2.5">
                    {a.status !== "approved" && a.status !== "rejected" ? (
                      <div className="flex justify-end gap-1.5">
                        {a.status === "submitted" && <button onClick={() => setAct({ app: a, status: "under_review" })} className="rounded-lg bg-amber-500/15 p-2 text-amber-300 hover:bg-amber-500/25" title="Mark under review" data-testid={`review-${a.application_no}`}><Eye size={15} /></button>}
                        <button onClick={() => setAct({ app: a, status: "approved" })} className="rounded-lg bg-emerald-500/15 p-2 text-emerald-300 hover:bg-emerald-500/25" title="Approve" data-testid={`approve-${a.application_no}`}><CheckCircle2 size={15} /></button>
                        <button onClick={() => setAct({ app: a, status: "rejected" })} className="rounded-lg bg-rose-500/15 p-2 text-rose-400 hover:bg-rose-500/25" title="Reject" data-testid={`reject-${a.application_no}`}><XCircle size={15} /></button>
                      </div>
                    ) : <p className="text-right font-mono text-xs text-slate-500">{a.admission_no || "—"}</p>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7}><Empty>No applications yet — share your portal link: /s/{"{code}"}</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!act} onClose={() => setAct(null)} title={act ? `${act.status.replace("_", " ")} — ${act.app.student_name}` : ""} testid="adm-action-modal">
        {act?.status === "approved" && <Select label={`Assign Section (Class ${act.app.class_applying})`} value={section} onChange={(e) => setSection(e.target.value)} data-testid="adm-section">{sections.map((s) => <option key={s}>{s}</option>)}</Select>}
        <div className="mt-3"><Field label="Remarks (sent to parent)" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder={act?.status === "approved" ? "Visit office with originals by 15 June" : ""} data-testid="adm-remarks" /></div>
        <button className="ep-btn mt-5 w-full" disabled={update.isPending} onClick={() => update.mutate()} data-testid="adm-confirm-btn">{update.isPending ? "Saving…" : act?.status === "approved" ? "Approve & Create Student" : "Confirm"}</button>
      </Modal>
    </>
  );
}
